<template>
  <div class="donation-cause-infinite-scroll">
    <v-list ref="listRef" class="custom-infinite-scroll-list">
      <v-list-item v-if="isSearchMode && displayedCauses.length === 0">
        <v-list-item-title>{{ t('donationCause.noCausesFound') }}</v-list-item-title>
      </v-list-item>
      <template
        v-for="(item, index) in displayedCauses"
        v-else
        :key="index"
      >
        <p v-if="item.header" class="cause-section-header">{{ item.header }}</p>
        <v-list-item
          v-else
          :title="''"
          @click="selectCause(item)"
        >
          <template #prepend>
            <v-avatar v-if="item.logoUrl && item.logoUrl.length > 0" size="40">
              <v-img
                :src="item.logoUrl"
                :alt="
                  t('donationDetails.form.donationCauseId.autoComplete.logoAltText', {
                    causeName: item.name,
                  })
                "
              />
            </v-avatar>
            <v-icon v-else size="40">mdi-charity</v-icon>
          </template>
          <v-list-item-title>{{ item.name }}</v-list-item-title>
          <template #append>
            <v-icon
              v-if="modelValue?.id === item.id"
              color="primary"
              size="20"
              style="opacity: 1;"
            >
              mdi-check-circle
            </v-icon>
          </template>
        </v-list-item>
      </template>
    </v-list>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, nextTick, watch } from 'vue';
import store from '@/store';
import { ENDPOINTS, get, guideErrorMessage, guideHttpStatus } from '@/api/mainApi';
import { DonationCause } from '@/interfaces/organizationgroup/donationCause';
import { getUserCountry } from '@/utils/userCountry';
import { useLabel } from '@/composables/useLabel';
import matomo from '@/services/matomoService';

export default defineComponent({
  props: {
    modelValue: { type: Object as () => DonationCause, default: null },
    searchQuery: { type: String, default: '' },
    searchResults: { type: Array as () => DonationCause[], default: () => [] },
  },
  emits: ['update:modelValue', 'select', 'ready'],
  setup(props, { emit }) {
    const { t } = useLabel();

    const listRef = ref();
    const paginatedCauses = ref<DonationCause[]>([]);
    const globalCauses = ref<DonationCause[]>([]);
    const tenantCauses = ref<DonationCause[]>([]);
    const loading = ref(false);
    const page = ref(1);
    const limit = ref(50);
    const totalPaginatedAvailable = ref(0);
    const hasAttachedListener = ref(false);
    const isSearchMode = computed(() => props.searchQuery && props.searchQuery.length >= 2);

    const displayedCauses = computed((): any[] => {
      if (isSearchMode.value) {
        return props.searchResults;
      }

      const items: any[] = [];
      const curatedIds = new Set<any>();

      if (globalCauses.value.length > 0) {
        items.push({ header: t('donationDetails.form.donationCauseId.autoComplete.popularCausesHeader') });
        for (const cause of globalCauses.value) {
          items.push(cause);
          curatedIds.add(cause.id);
        }
      }

      if (tenantCauses.value.length > 0) {
        items.push({ header: t('donationDetails.form.donationCauseId.autoComplete.tenantCausesHeader') });
        for (const cause of tenantCauses.value) {
          if (!curatedIds.has(cause.id)) {
            items.push(cause);
            curatedIds.add(cause.id);
          }
        }
      }

      const filteredPaginated = paginatedCauses.value.filter(c => !curatedIds.has(c.id));
      if (filteredPaginated.length > 0) {
        if (globalCauses.value.length > 0 || tenantCauses.value.length > 0) {
          items.push({ header: t('donationGuide.certificateDetails.listOfOrganizations') });
        }
        items.push(...filteredPaginated);
      }

      return items;
    });

    const recipientId = computed(() => {
      return store.state.requestSession?.donationCases?.[0]?.recipient?.id ?? null;
    });

    const fetchCauses = async (endpoint: string, params) => {
      try {
        const response = await get(endpoint, params);
        return response || {};
      } catch (error) {
        window.log?.error('[Guide] donationCauses.fetch — Failed to fetch donation causes', {
          message: guideErrorMessage(error),
          httpStatus: guideHttpStatus(error),
          endpoint: endpoint.split('?')[0],
        });
        return {
          paginatedResponse: { data: [], total: 0 },
        };
      }
    };

    const loadCauses = async (endpoint: string, extraParams = {}) => {
      const country = getUserCountry();
      const params = {
        onlyActive: true,
        country,
        ...(recipientId.value != null && { recipientId: recipientId.value }),
        ...extraParams,
      };
      return await fetchCauses(endpoint, params);
    };

    const getInitialCauses = async () => {
      const response = await loadCauses(ENDPOINTS.INITIAL_DONATION_CAUSES, { limit: 5 });
      const curated = response?.curatedCauseResult;
      globalCauses.value = curated?.globalCauses || [];
      tenantCauses.value = curated?.tenantCauses || [];
      const total = response?.paginatedResponse?.total || response?.paginatedResponse?.totalRowCount || 0;
      if (total > 0) totalPaginatedAvailable.value = total;
    };

    const getPaginatedCauses = async () => {
      if (
        loading.value ||
        (totalPaginatedAvailable.value > 0 &&
          paginatedCauses.value.length >= totalPaginatedAvailable.value)
      ) {
        return;
      }
      loading.value = true;

      let endpoint = ENDPOINTS.INITIAL_DONATION_CAUSES;
      let extraParams = {
        limit: limit.value,
        page: page.value,
      };

      // If there's a search query with 2 or more characters, use the search endpoint
      if (props.searchQuery && props.searchQuery.length >= 2) {
        endpoint = `${ENDPOINTS.DONATION_CAUSES}/${ENDPOINTS.SEARCH}/${props.searchQuery}`;
        extraParams = {
          limit: limit.value,
          page: page.value,
        };
      }

      const response = await loadCauses(endpoint, extraParams);
      const newCauses = response?.paginatedResponse?.data || [];
      if (newCauses.length > 0) {
        paginatedCauses.value.push(...newCauses);
        page.value += 1;
        const total =
          response?.paginatedResponse?.total || response?.paginatedResponse?.totalRowCount;
        if (total) totalPaginatedAvailable.value = total;
      } else {
        // No more causes to load, set total to current count
        totalPaginatedAvailable.value = paginatedCauses.value.length;
      }
      loading.value = false;
    };

    const selectCause = (cause: DonationCause) => {
      // Ignore repeat clicks on the charity that is already selected
      if (cause.id !== props.modelValue?.id) {
        matomo.trackEvent('donations_certificate_charity_change', 'certificate', 'donations_certificate_charity_change');
      }
      emit('update:modelValue', cause);
      emit('select', cause);
    };

    const attachScrollListener = () => {
      if (hasAttachedListener.value) return;
      
      // Find the modal content container (the scrolling parent)
      const listElement = listRef.value?.$el as HTMLElement;
      const scrollContainer = listElement?.closest('.charity-modal-content') as HTMLElement;
      
      if (scrollContainer && !scrollContainer.dataset.listenerAttached) {
        // console.log('Attaching scroll listener to modal content container');
        scrollContainer.addEventListener('scroll', onVirtualScroll);
        scrollContainer.dataset.listenerAttached = 'true';
        hasAttachedListener.value = true;
      } else if (!scrollContainer) {
        // Modal not mounted yet; polling will retry
      } else if (scrollContainer.dataset.listenerAttached) {
        hasAttachedListener.value = true;
      }
    };

    const attachScrollListenerWithPolling = () => {
      // Always try to attach scroll listener
      // Reset the attached flag to allow reattachment
      hasAttachedListener.value = false;
      
      // Poll every 100ms until modal container is found and listener attached
      let pollCount = 0;
      const maxPolls = 50; // Stop after 5 seconds
      
      const pollForList = () => {
        if (hasAttachedListener.value) return; // Already attached
        
        if (pollCount >= maxPolls) {
          return;
        }
        
        pollCount++;
        attachScrollListener();
        
        if (!hasAttachedListener.value) {
          // Keep polling if not attached yet
          setTimeout(pollForList, 100);
        } else {
          // console.log('Scroll listener attached after', pollCount, 'polls');
        }
      };
      
      // Start polling immediately
      pollForList();
    };

    const onVirtualScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      const scrollThreshold = 50;
      const currentScroll = target.scrollTop + target.clientHeight;
      const scrollTrigger = target.scrollHeight - scrollThreshold;
      
      const shouldLoad =
        currentScroll >= scrollTrigger &&
        !loading.value &&
        !isSearchMode.value &&
        paginatedCauses.value.length < totalPaginatedAvailable.value;


      if (shouldLoad) {
        getPaginatedCauses();
      }
    };

    onMounted(async () => {
      // console.log('DonationCauseInfiniteScrollComponent mounted - starting to load causes');
      // Load curated causes first, then paginated
      await getInitialCauses();
      await getPaginatedCauses();
      emit('ready');

      // Attach scroll listener with polling to ensure it works
      nextTick(() => {
        setTimeout(attachScrollListenerWithPolling, 500);
      });
    });

    // Watch for when search is cleared to reload paginated results
    watch(() => props.searchQuery, (newQuery, oldQuery) => {
      if (newQuery && newQuery.length >= 2 && newQuery !== oldQuery) {
        matomo.trackEvent('donations_certificate_charity_search', 'certificate', 'donations_certificate_charity_search');
      }
      // If search was cleared (had value, now empty)
      if (oldQuery && oldQuery.length > 0 && (!newQuery || newQuery.length === 0)) {
        // Reset pagination state
        paginatedCauses.value = [];
        page.value = 1;
        totalPaginatedAvailable.value = 0;
        // Reload initial causes
        getPaginatedCauses();
      }
    });

    return {
      listRef,
      paginatedCauses,
      globalCauses,
      tenantCauses,
      loading,
      page,
      limit,
      totalPaginatedAvailable,
      displayedCauses,
      isSearchMode,
      recipientId,
      selectCause,
      getInitialCauses,
      getPaginatedCauses,
      attachScrollListener,
      attachScrollListenerWithPolling,
      onVirtualScroll,
      t,
    };
  },
});
</script>

<style scoped>
.donation-cause-infinite-scroll {
  max-height: 400px;
  overflow-y: auto;
}

.custom-infinite-scroll-list {
  padding: 0;
}

.cause-section-header {
  font-family: "Lato", sans-serif;
  font-weight: 700;
  color: #6B7280;
  font-size: 14px;
  margin: 12px 0 8px 0;
  padding: 0;
}

:deep(.v-list-item__append) {
  opacity: 1;
}
</style>
